/* ***************************************************************************
 * attachmentRebalance — CON-1929
 *
 * One path for every multi-attachment add: the file picker, a paste, several
 * files at once or one at a time. Each add re-plans the WHOLE message against
 * the budget (planRebalance) and recompresses any attachment that is now over
 * its share, so the set fits roughly evenly instead of the last file getting
 * whatever was left.
 *
 * WHY THE CLIENT AND NOT THE SERVER. Compression already happens here, at
 * upload, and the composer already refuses an over-budget add before anything
 * reaches the server. Recompressing on the server would put image work into
 * the create controllers or the send path, the riskiest code we have, and the
 * client would still have to stop refusing first. Here the send path does not
 * change at all, and the server's own size check stays as the backstop.
 *
 * WHERE THE PIXELS COME FROM. The composer keeps the original File of
 * everything picked or pasted in this session (originals map) and recompresses
 * from that, so a picture shrunk to 150 KB and then to 100 KB is cut once from
 * the original, not twice. An attachment with no original in memory (a
 * restored draft, a scheduled message being edited) is fetched back from its
 * blob and recompressed from that.
 *
 * PNG. A PNG is lossless, so the only way to shrink it is to drop pixels; a
 * 200 KB screenshot squeezed to 100 KB as a PNG is blurred. When an image has
 * to be compressed here, a PNG with no transparency is converted to JPEG
 * (renamed .jpg), which keeps it sharp at a fraction of the size. A PNG with
 * transparent pixels stays a PNG, because JPEG would paint them black.
 *
 * Only ever called when the message will carry two or more attachments; a
 * single attachment keeps the upload path it always had.
 *************************************************************************** */

import { compressImage } from 'src/utils/imageCompression';
import { MESSAGES } from 'src/constants';
import { planRebalance, fetchBlobSizeBytes, isBudgetCompressible } from 'src/helpers/attachmentBudget';

const { VCARD_FILENAME } = MESSAGES;

// CON-1936 — no gif: a GIF is never shrunk (see isBudgetCompressible).
const COMPRESSIBLE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const UPLOAD_FAILED = 'Failed to upload file. Please try again.';

const extensionOf = (name) => (String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || '';

/* A stored blob has no File to ask, so its type is read from its name. */
export const isCompressibleName = (name) => (
  !!name && name !== VCARD_FILENAME && COMPRESSIBLE_EXTENSIONS.includes(extensionOf(name))
);

/* photo.png -> photo.jpg; a name with no extension gets one. */
export const toJpegName = (name) => {
  const base = String(name || 'image');
  return /\.[a-z0-9]+$/i.test(base) ? base.replace(/\.[a-z0-9]+$/i, '.jpg') : `${base}.jpg`;
};

/* A stored blob's name carries the upload prefix (EO-ID-<timestamp>-). A
 * file fetched back to be recompressed is uploaded again, which would add a
 * second prefix (EO-ID-...-EO-ID-...-shot.jpg) and show it in the chip, so
 * the fetched copy is named after the original file. */
const UPLOAD_PREFIX = /^EO-ID-\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-(?:\d{1,3}-)?/;
export const originalNameOf = (blobName) => {
  let name = String(blobName || '');
  while (UPLOAD_PREFIX.test(name)) {
    name = name.replace(UPLOAD_PREFIX, '');
  }
  return name || String(blobName || '');
};

/* ***************************************************************************
 * hasTransparency
 * True when any sampled pixel is not fully opaque. Read on a small copy (at
 * most 256 px on the long side), which is plenty to find a transparent
 * background. Any failure answers true: keeping a PNG is always safe.
 *************************************************************************** */
export const hasTransparency = async (file) => {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 256 / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);
    const { data } = ctx.getImageData(0, 0, width, height);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
    return false;
  } catch (e) {
    return true;
  }
};

/* ***************************************************************************
 * CON-1930 — sizing an image to a target in a few encodes, not twenty.
 *
 * The shared compressImage shrinks a full-size picture by 10% and re-encodes
 * it until it fits: a 12-megapixel photo squeezed to 100 KB takes about
 * seventeen encodes, and a rebalance does that for every picture on the
 * text. Encoded size grows with the picture's area, so the scale that hits a
 * target can be estimated from one encode: scale x sqrt(target / bytes).
 * firstScale guesses a starting point from the pixel count, nextScale
 * corrects it from what an encode actually produced; one to three encodes is
 * typical. The picture is decoded once (createImageBitmap) and that decode is
 * shared with the transparency check. compressImage itself is untouched:
 * this runs only for the multi-attachment beta's rebalance.
 *************************************************************************** */
const ENCODE_QUALITY = 0.8;
const MAX_ENCODES = 8;
const AIM = 0.92; // aim a little under the target so the next encode fits

export const firstScale = ({
  width, height, target, bytesPerPixel = 0.25,
}) => {
  const pixels = Math.max(1, width * height);
  return Math.min(1, Math.sqrt(target / (pixels * bytesPerPixel)));
};

export const nextScale = ({ scale, bytes, target }) => (
  Math.min(1, scale * Math.sqrt((target * AIM) / Math.max(1, bytes)))
);

const encodeAt = (bitmap, scale, mimeType) => new Promise((resolve) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  canvas.toBlob((blob) => resolve(blob), mimeType, ENCODE_QUALITY);
});

/* The largest encode found at or under the target, or null. */
export const encodeToTarget = async ({ bitmap, target, mimeType }) => {
  let scale = firstScale({ width: bitmap.width, height: bitmap.height, target });
  let best = null;
  for (let i = 0; i < MAX_ENCODES; i++) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await encodeAt(bitmap, scale, mimeType);
    if (!blob) return best;
    if (blob.size <= target) {
      if (!best || blob.size > best.size) best = blob;
      const grown = nextScale({ scale, bytes: blob.size, target });
      // Close enough, already full size, or no real room to grow: done.
      if (blob.size >= target * 0.75 || scale >= 1 || grown <= scale * 1.03) return best;
      scale = grown;
    } else {
      if (best) return best;
      scale = Math.min(nextScale({ scale, bytes: blob.size, target }), scale * 0.95);
      if (bitmap.width * scale < 50) return null;
    }
  }
  return best;
};

const bitmapHasTransparency = (bitmap) => {
  const scale = Math.min(1, 256 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
};

const tooBigMessage = (target) => `Image could not be compressed to under ${Math.round(target / 1024)}KB. Please use a smaller image.`;

/* JPEG and PNG only; anything else, or a browser without createImageBitmap,
 * answers null and the caller falls back to compressImage. */
const compressFast = async (file, target) => {
  const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg';
  const isPng = file.type === 'image/png';
  if ((!isJpeg && !isPng) || typeof createImageBitmap !== 'function') return null;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (e) {
    return null;
  }
  try {
    const toJpeg = isPng && !bitmapHasTransparency(bitmap);
    const mimeType = isJpeg || toJpeg ? 'image/jpeg' : 'image/png';
    const blob = await encodeToTarget({ bitmap, target, mimeType });
    if (!blob) return { ok: false, file: null, error: tooBigMessage(target) };
    const name = toJpeg ? toJpegName(file.name) : file.name;
    return { ok: true, file: new File([blob], name, { type: mimeType }), error: null };
  } catch (e) {
    return null;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
};

/* ***************************************************************************
 * compressForBudget
 * One file to one target. Under the target: returned as it is (a PNG that
 * already fits stays a PNG). Over it: compressed, and a PNG without
 * transparency comes out as JPEG.
 * Returns { ok, file, error }.
 *************************************************************************** */
export const compressForBudget = async (file, target) => {
  if (!file || !isBudgetCompressible(file) || !target || file.size <= target) {
    return { ok: true, file, error: null };
  }
  const fast = await compressFast(file, target);
  if (fast) return fast;
  const toJpeg = file.type === 'image/png' && !(await hasTransparency(file));
  const result = await compressImage(file, target, toJpeg ? 'image/jpeg' : null);
  if (!result.success) {
    return { ok: false, file: null, error: result.error };
  }
  const name = toJpeg ? toJpegName(file.name) : file.name;
  const type = toJpeg ? 'image/jpeg' : file.type;
  return { ok: true, file: new File([result.blob], name, { type }), error: null };
};

/* ***************************************************************************
 * fetchBlobAsFile
 * A stored attachment back as a File, for recompressing when its original
 * is not in memory.
 *************************************************************************** */
export const fetchBlobAsFile = async (url, name) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`could not read ${name}`);
  const blob = await response.blob();
  return new File([blob], name, { type: blob.type || 'application/octet-stream' });
};

/* ***************************************************************************
 * rebalanceAttachments
 *
 * @param existing    [{ name, original? }] attachments already on the
 *                    message, in order (blob names; original is the File it
 *                    was made from, when this session still has it)
 * @param newFiles    File[] being added, in order
 * @param totalBytes  the channel budget
 * @param deps        { getUrl(name), upload(file) -> blobName|null,
 *                      fetchSize?, fetchFile?, compress? } (the last three
 *                      default to the real ones; tests replace them)
 * @returns { ok, error, names, originals }  names is the full new list in
 *          order (existing first, then new); originals maps each NEW blob
 *          name to the File it was cut from, to merge into the caller's map.
 *          Nothing on the message changes unless ok is true.
 *************************************************************************** */
export const rebalanceAttachments = async ({
  existing = [],
  newFiles = [],
  totalBytes,
  deps,
}) => {
  const {
    getUrl,
    upload,
    fetchSize = fetchBlobSizeBytes,
    fetchFile = fetchBlobAsFile,
    compress = compressForBudget,
  } = deps || {};

  // CON-1934 — the contact card counts at its real size (with a photo about
  // 70 KB) as a fixed file: it is never compressible (isCompressibleName).
  // getUrl must read it from the user's own container (containerForFile).
  const existingSizes = await Promise.all(existing.map(({ name }) => fetchSize(getUrl(name))));

  const items = [
    ...existing.map(({ name }, i) => ({
      kind: 'existing', name, size: existingSizes[i], compressible: isCompressibleName(name),
    })),
    ...newFiles.map((file) => ({
      kind: 'new', file, size: file.size, compressible: isBudgetCompressible(file),
    })),
  ];

  const plan = planRebalance({ items, totalBytes });
  if (!plan.ok) {
    return {
      ok: false, error: plan.error, names: [], originals: new Map(),
    };
  }

  const prepareItem = async (item, target, existingEntry) => {
    let source;
    if (item.kind === 'existing') {
      source = existingEntry.original;
      if (!source) {
        try {
          source = await fetchFile(getUrl(item.name), originalNameOf(item.name));
        } catch (e) {
          return { ok: false, error: UPLOAD_FAILED };
        }
      }
    } else {
      source = item.file;
    }

    const compressed = target ? await compress(source, target) : { ok: true, file: source };
    if (!compressed.ok) {
      return { ok: false, error: compressed.error };
    }
    return { ok: true, file: compressed.file, source };
  };

  /* CON-1930 — pictures are compressed one at a time (each decode can hold
   * tens of MB, so not all at once), but each upload starts the moment its
   * file is ready and runs while the next picture compresses. */
  const failed = (error) => ({
    ok: false, error, names: [], originals: new Map(),
  });
  const slots = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const target = plan.targets[i];

    if (item.kind === 'existing' && !target) {
      slots.push({ keep: item.name });
    } else {
      // eslint-disable-next-line no-await-in-loop
      const prepared = await prepareItem(item, target, existing[i]);
      if (!prepared.ok) {
        // Let uploads already started finish before reporting.
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(slots.map((slot) => slot.uploading));
        return failed(prepared.error);
      }
      const uploading = Promise.resolve()
        .then(() => upload(prepared.file))
        .catch(() => null);
      slots.push({ uploading, source: prepared.source });
    }
  }

  const uploadedNames = await Promise.all(slots.map((slot) => slot.uploading || null));
  if (slots.some((slot, i) => !slot.keep && !uploadedNames[i])) {
    return failed(UPLOAD_FAILED);
  }
  const names = slots.map((slot, i) => slot.keep || uploadedNames[i]);
  const originals = new Map();
  slots.forEach((slot, i) => {
    if (!slot.keep) originals.set(uploadedNames[i], slot.source);
  });

  return {
    ok: true, error: null, names, originals,
  };
};
